import { Text, TextInput, Select, SelectItem } from '@tremor/react';
import type { MinMaxParams } from '../api/types';
import { parseLowestMin } from '../api/minmax';
import { t } from '../lib/i18n';

/**
 * JCD-56 — the recalculation parameter form, shared by the single-part panel
 * and the batch page. It is a controlled component over MinMaxParams and owns
 * no state of its own except the raw text of the "lowest min" field, whose
 * legacy grammar ("5", "s", "3s") does not map to a single number.
 *
 * Validation lives in api/minmax.ts so the form and the batch page cannot
 * drift; the server re-checks everything regardless.
 */
interface Props {
  value: MinMaxParams;
  onChange: (next: MinMaxParams) => void;
  /** The raw text of the lowest-min field, owned by the parent so a partial
   *  entry like "3" on the way to "3s" is not destroyed on every keystroke. */
  lowestMinText: string;
  onLowestMinTextChange: (s: string) => void;
  disabled?: boolean;
}

export default function MinMaxParamsForm({
  value, onChange, lowestMinText, onLowestMinTextChange, disabled,
}: Props) {
  const set = (patch: Partial<MinMaxParams>) => onChange({ ...value, ...patch });
  const num = (s: string) => (s.trim() === '' ? 0 : Number(s));

  const onLowestMin = (s: string) => {
    onLowestMinTextChange(s);
    const parsed = parseLowestMin(s);
    if (parsed) set(parsed);
  };

  return (
    <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
      <div>
        <label htmlFor="mm-season" className="block text-sm">{t.part.minmax.params.season}</label>
        <Select
          id="mm-season"
          disabled={disabled}
          value={String(value.season)}
          onValueChange={(v) => set({ season: v === '2' ? 2 : 1 })}
        >
          <SelectItem value="1">1</SelectItem>
          <SelectItem value="2">2</SelectItem>
        </Select>
      </div>

      <div>
        <label htmlFor="mm-per-start" className="block text-sm">
          {t.part.minmax.params.periods} ({t.part.minmax.params.from})
        </label>
        <TextInput
          id="mm-per-start"
          disabled={disabled}
          value={String(value.per_start)}
          onChange={(e) => set({ per_start: num(e.target.value) })}
        />
      </div>
      <div>
        <label htmlFor="mm-per-end" className="block text-sm">
          {t.part.minmax.params.periods} ({t.part.minmax.params.to})
        </label>
        <TextInput
          id="mm-per-end"
          disabled={disabled}
          value={String(value.per_end)}
          onChange={(e) => set({ per_end: num(e.target.value) })}
        />
      </div>

      <div>
        <label htmlFor="mm-weeks" className="block text-sm">{t.part.minmax.params.weeks}</label>
        <TextInput
          id="mm-weeks"
          disabled={disabled}
          value={String(value.weeks)}
          onChange={(e) => set({ weeks: num(e.target.value) })}
        />
      </div>
      <div>
        <label htmlFor="mm-days" className="block text-sm">{t.part.minmax.params.days}</label>
        <TextInput
          id="mm-days"
          disabled={disabled}
          value={String(value.days)}
          onChange={(e) => set({ days: num(e.target.value) })}
        />
      </div>

      <div>
        <label htmlFor="mm-min-days" className="block text-sm">{t.part.minmax.params.minDays}</label>
        <TextInput
          id="mm-min-days"
          disabled={disabled}
          value={String(value.min_days)}
          onChange={(e) => set({ min_days: num(e.target.value) })}
        />
      </div>
      <div>
        <label htmlFor="mm-max-days" className="block text-sm">{t.part.minmax.params.maxDays}</label>
        <TextInput
          id="mm-max-days"
          disabled={disabled}
          value={String(value.max_days)}
          onChange={(e) => set({ max_days: num(e.target.value) })}
        />
      </div>

      <div>
        <label htmlFor="mm-min-round" className="block text-sm">{t.part.minmax.params.minRound}</label>
        <Select
          id="mm-min-round"
          disabled={disabled}
          value={value.min_round}
          onValueChange={(v) => set({ min_round: v as MinMaxParams['min_round'] })}
        >
          <SelectItem value="U">{t.part.minmax.round.U}</SelectItem>
          <SelectItem value="D">{t.part.minmax.round.D}</SelectItem>
          <SelectItem value="O">{t.part.minmax.round.O}</SelectItem>
        </Select>
      </div>
      <div>
        <label htmlFor="mm-max-round" className="block text-sm">{t.part.minmax.params.maxRound}</label>
        <Select
          id="mm-max-round"
          disabled={disabled}
          value={value.max_round}
          onValueChange={(v) => set({ max_round: v as MinMaxParams['max_round'] })}
        >
          <SelectItem value="U">{t.part.minmax.round.U}</SelectItem>
          <SelectItem value="D">{t.part.minmax.round.D}</SelectItem>
          <SelectItem value="O">{t.part.minmax.round.O}</SelectItem>
        </Select>
      </div>

      <div>
        <label htmlFor="mm-pack-round" className="block text-sm">{t.part.minmax.params.packRound}</label>
        <Select
          id="mm-pack-round"
          disabled={disabled}
          value={value.pack_round}
          onValueChange={(v) => set({ pack_round: v as MinMaxParams['pack_round'] })}
        >
          <SelectItem value="N">{t.part.minmax.pack.N}</SelectItem>
          <SelectItem value="U">{t.part.minmax.pack.U}</SelectItem>
          <SelectItem value="D">{t.part.minmax.pack.D}</SelectItem>
          <SelectItem value="O">{t.part.minmax.pack.O}</SelectItem>
          <SelectItem value="OU">{t.part.minmax.pack.OU}</SelectItem>
        </Select>
      </div>

      <div>
        <label htmlFor="mm-lowest-min" className="block text-sm">{t.part.minmax.params.lowestMin}</label>
        <TextInput
          id="mm-lowest-min"
          disabled={disabled}
          value={lowestMinText}
          onChange={(e) => onLowestMin(e.target.value)}
        />
        <Text className="text-xs">{t.part.minmax.params.lowestMinHint}</Text>
      </div>

      <div>
        <label htmlFor="mm-mult-sell" className="block text-sm">{t.part.minmax.params.multSell}</label>
        <Select
          id="mm-mult-sell"
          disabled={disabled}
          value={value.mult_sell ? '1' : '0'}
          onValueChange={(v) => set({ mult_sell: v === '1' })}
        >
          <SelectItem value="1">Sí</SelectItem>
          <SelectItem value="0">No</SelectItem>
        </Select>
      </div>

      <div>
        <label htmlFor="mm-max-one" className="block text-sm">{t.part.minmax.params.maxOne}</label>
        <TextInput
          id="mm-max-one"
          disabled={disabled}
          value={value.max_one === null || value.max_one === undefined ? '' : String(value.max_one)}
          onChange={(e) =>
            set({ max_one: e.target.value.trim() === '' ? null : num(e.target.value) })
          }
        />
      </div>

      <div>
        <label htmlFor="mm-pct" className="block text-sm">{t.part.minmax.params.pctThreshold}</label>
        <TextInput
          id="mm-pct"
          disabled={disabled}
          value={String(value.pct_threshold)}
          onChange={(e) => set({ pct_threshold: num(e.target.value) })}
        />
      </div>

      <div>
        <label htmlFor="mm-all-stores" className="block text-sm">{t.part.minmax.params.allStores}</label>
        <Select
          id="mm-all-stores"
          disabled={disabled}
          value={value.all_stores_history ? '1' : '0'}
          onValueChange={(v) => set({ all_stores_history: v === '1' })}
        >
          <SelectItem value="1">Sí</SelectItem>
          <SelectItem value="0">No</SelectItem>
        </Select>
      </div>
    </div>
  );
}
